
import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { toast } from 'react-toastify';
import React from 'react';
import { MILESTONE_TYPES, SKIP_MILESTONE_DETAILS } from '../../utils/constants/MilestoneConstants';
import { isRealtimeWsEnabled } from '../../realtime/config';

// Helper to fetch supplier stats after dealfinancing
export const fetchSupplierStatsAfterDealFinancing = async (orgId) => {
    try {
        const baseUrlUser = process.env.REACT_APP_USER_MANAGEMENT;
        const response = await axios.get(`${baseUrlUser}/supplierstats?orgId=${orgId}`);
        if (response.status === 200) {
            return response.data.payload;
        }
        return null;
    } catch (error) {
        return null;
    }
};

const baseUrlTra = process.env.REACT_APP_TRANSACTIONS_MANAGEMENT;
let apiInterval;
let milestoneWsUnsub = null;
let milestoneFallbackTimer = null;

const clearMilestoneWatchers = () => {
    if (apiInterval) {
        clearInterval(apiInterval);
        apiInterval = null;
    }
    if (milestoneFallbackTimer) {
        clearTimeout(milestoneFallbackTimer);
        milestoneFallbackTimer = null;
    }
    if (milestoneWsUnsub) {
        milestoneWsUnsub();
        milestoneWsUnsub = null;
    }
};

const isMatchingTransaction = (envelope, id) => {
    const tx = envelope.payload?.transactionUlid || envelope.payload?.transaction_ulid;
    return !tx || !id || tx === id;
};

const handleTransactionUpdated = async (envelope, id, dispatch, milestone, watchState) => {
    if (!isMatchingTransaction(envelope, id)) {
        return;
    }
    const res = await dispatch(gettransactionByID({ id: id, isUpdateRequired: true }));
    const current = res?.payload?.payload?.current_milestone;
    if (current && current !== milestone) {
        watchState.milestoneChanged = true;
        clearMilestoneWatchers();
    }
};

const subscribeRealtimeMilestone = (id, dispatch, milestone, watchState, startPolling) => {
    import('../../realtime')
        .then(({ getRealtimeService, REALTIME_EVENT_TYPES }) => {
            const service = getRealtimeService();
            if (!service) {
                startPolling();
                return;
            }
            if (id) {
                service.subscribeChannels([`transaction:${id}`]);
            }
            milestoneWsUnsub = service.subscribe(REALTIME_EVENT_TYPES.TRANSACTION_UPDATED, (envelope) =>
                handleTransactionUpdated(envelope, id, dispatch, milestone, watchState)
            );
        })
        .catch(() => startPolling());
};

const scheduleMilestoneFallback = (id, dispatch, watchState) => {
    if (milestoneFallbackTimer) {
        clearTimeout(milestoneFallbackTimer);
    }
    milestoneFallbackTimer = setTimeout(() => {
        milestoneFallbackTimer = null;
        if (!watchState.milestoneChanged) {
            dispatch(gettransactionByID({ id: id, isUpdateRequired: false }));
        }
        clearMilestoneWatchers();
    }, 50000);
};

const startMilestonePolling = (id, dispatch, milestone, watchState) => {
    if (apiInterval) {
        return;
    }
    apiInterval = setInterval(async () => {
        try {
            const apiData = { id: id, isUpdateRequired: true };
            const res = await dispatch(gettransactionByID(apiData));
            if (res.payload.payload.current_milestone !== milestone) {
                watchState.milestoneChanged = true;
                clearMilestoneWatchers();
            }
        } catch (error) {
            const apiData = { id: id, isUpdateRequired: false };
            dispatch(gettransactionByID(apiData));
            clearMilestoneWatchers();
        }
    }, 10000);

    scheduleMilestoneFallback(id, dispatch, watchState);
};

const useIntervalDispatcher = (id, dispatch, milestone, status) => {
    const watchState = { milestoneChanged: false };
    if (status === 'success') {
        dispatch(gettransactionByID({ id: id, isUpdateRequired: true }));
        clearMilestoneWatchers();

        const startPolling = () => startMilestonePolling(id, dispatch, milestone, watchState);

        if (isRealtimeWsEnabled()) {
            subscribeRealtimeMilestone(id, dispatch, milestone, watchState, startPolling);
        }

        startPolling();
        return;
    }

    if (status === 'fail') {
        clearMilestoneWatchers();
        dispatch(gettransactionByID({ id: id, isUpdateRequired: false }));
        watchState.milestoneChanged = false;
    }
};

export const getFinanceCostSimulator = createAsyncThunk('getFinanceCostSimulator', async (req, thunkAPI) => {
    try {
        const response = await axios.post(`${baseUrlTra}/financeCostSimulator`, req);
        const { payload } = response.data;
        if (response) {
            return payload;
        }
        return payload;
    } catch (error) {
        return thunkAPI.rejectWithValue(error);
    }
});
export const gettransactionByID = createAsyncThunk('gettransactionByID', async (data, thunkAPI) => {
    const { id, isUpdateRequired } = data;
    try {
        const response = await axios.get(`${baseUrlTra}/getTransactionByID/${id}`);
        const { payload } = response.data;
        return { payload, isUpdateRequired };
    } catch (error) {
        return thunkAPI.rejectWithValue({
            status: error?.response?.status,
            serviceMessage: error?.response?.data?.serviceMessage || error?.message,
            responseCode: error?.response?.data?.responseCode,
        });
    }
});

export const getAlltransactionOps = createAsyncThunk('getAlltransactionOps', async (param, thunkAPI) => {
    try {
        const { append, reset, ...queryParams } = param || {};
        const response = await axios.get(`${baseUrlTra}/getAllTransactions`, {
            params: queryParams,
        });
        const result = response.data;
        const payload = result?.payload || {};
        const { has_next, next_cursor } = payload;
        const totalCount =
            payload.totalCount ??
            payload.total_count ??
            result?.totalCount ??
            result?.total_count ??
            payload.pagination?.totalCount ??
            payload.pagination?.total_count;
        const allTrans = payload.transactions;

        return {
            resp: allTrans,
            requestedCursor: queryParams?.cursor ?? param?.cursor ?? 0,
            nextPage: { has_next, next_cursor, totalCount },
            append: Boolean(append),
            reset: Boolean(reset),
        };
    } catch (error) {
        return thunkAPI.rejectWithValue(error);
    }
});

export const updateTransactionDeal = createAsyncThunk('updateTransactionDeal', async (data, thunkAPI) => {
    const { id, type, setSupDealConfirmationBtn, handleNext } = data;
    try {
        const response = await axios.put(`${baseUrlTra}/confirmDealBy${type}/${id}`);
        const result = response.data;

        if (result) {
            toast.success(result.payload, {
                icon: <span className='msg-icon float-start ms-2 sf-check success-icon'></span>,
            });

            setSupDealConfirmationBtn(true);
            localStorage.setItem('supDealConfirmationulid', id);

            handleNext();
        }

        return result;
    } catch (error) {
        toast.error(error.message);
        return thunkAPI.rejectWithValue(error);
    }
});

export const generateSOPO = createAsyncThunk('generateSOPO', async (data, thunkAPI) => {
    const { id, type, milestone, req, dispatch } = data;
    useIntervalDispatcher(id, dispatch, milestone, 'success');

    try {
        const response = await axios.put(`${baseUrlTra}/transaction/${type}/${id}`, req);
        const payload = response.data;
        return payload;
    } catch (error) {
        toast.error(error.message);
        useIntervalDispatcher(id, dispatch, milestone, 'fail');
        return thunkAPI.rejectWithValue(error);
    }
});

export const skipSOPO = createAsyncThunk('skipSOPO', async (data, thunkAPI) => {
    const { id, milestone, dispatch } = data;
    useIntervalDispatcher(id, dispatch, milestone, 'success');

    try {
        const response = await axios.patch(`${baseUrlTra}/skip/transactions/${id}/milestones/${milestone}`);
        const payload = response.data;

        let successMessage = '';

        if (response.status === 200) {
            if (milestone === MILESTONE_TYPES.PURCHASE_ORDER) {
                successMessage = SKIP_MILESTONE_DETAILS.SKIP_PO_DESCRIPTION;
            } else if (milestone === MILESTONE_TYPES.SALES_ORDER) {
                successMessage = SKIP_MILESTONE_DETAILS.SKIP_SO_DESCRIPTION;
            }
            toast.success(successMessage, {
                icon: <i className='bi bi-check-circle-fill' style={{ color: '#409895' }}></i>,
            });
        }

        return payload;
    } catch (error) {
        const errorMessage = error.response?.data?.serviceMessage || error.message;
        toast.error(errorMessage);
        useIntervalDispatcher(id, dispatch, milestone, 'fail');
        return thunkAPI.rejectWithValue(errorMessage);
    }
});

export const getBDNsDetails = createAsyncThunk('getBDNsDetails', async (id, thunkAPI) => {
    try {
        const response = await axios.get(`${baseUrlTra}/bdns/${id}`);
        return response.data;
    } catch (error) {
        const errorMessage = error.response?.data?.serviceMessage || error.message;
        return thunkAPI.rejectWithValue(errorMessage);
    }
});

export const uploadBDNBySup = createAsyncThunk(
    'uploadBDNBySup',
    async (req, thunkAPI) => {
        const {
            id,
            data,
            dispatch,
            status,
            setDisabledBDNBtn,
            handleNext,
            setIsLoading
        } = req;

        try {
            const response = await axios.put(
                `${baseUrlTra}/bdn/uploadbdn/${id}`,
                data,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );

            if (response) {
                toast.success(response.data.serviceMessage, {
                    icon: (
                        <span className='msg-icon float-start ms-2 sf-check success-icon'></span>
                    ),
                });

                dispatch(getBDNsDetails(id));
                dispatch(gettransactionByID({ id, isUpdateRequired: true }));

                if (status === 'active') {
                    localStorage.setItem('supFormBDNUlid', id);
                    setDisabledBDNBtn(true);
                    handleNext();
                }
            }

            setIsLoading(false);  // <--- stop loader (success)
            return response.data;

        } catch (error) {
            const errorMessage =
                error.response?.data?.serviceMessage || error.message;

            toast.error(errorMessage);

            setIsLoading(false);  // <--- stop loader (error)
            return thunkAPI.rejectWithValue(errorMessage);
        }
    }
);

export const saveBDNBySup = createAsyncThunk('saveBDNBySup', async (req, thunkAPI) => {
    const { id, data, dispatch, milestone } = req;
    useIntervalDispatcher(id, dispatch, milestone, 'success');
    try {
        const response = await axios.put(`${baseUrlTra}/bdn/savebdn/${id}`, data, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });

        if (response) {
            const apiData = { id: id, isUpdateRequired: true };
            dispatch(gettransactionByID(apiData));
            dispatch(getBDNsDetails(id));

        }
        const payload = response.data;
        return payload;
    } catch (error) {
        const errorMessage = error.response?.data?.serviceMessage || error.message;
        toast.error(errorMessage);
        useIntervalDispatcher(id, dispatch, milestone, 'fail');
        return thunkAPI.rejectWithValue(errorMessage);
    }
});

export const saveBDNSBySup = createAsyncThunk('saveBDNSBySup', async (req, thunkAPI) => {
    const { id, data, dispatch, milestone, saveAsDraft } = req;
    useIntervalDispatcher(id, dispatch, milestone, 'success');
    try {
        const response = await axios.put(`${baseUrlTra}/bdn/savebdns/${id}/${saveAsDraft}`, data, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });

        if (response) {
            toast.success(response.serviceMessage || response?.data?.serviceMessage, {
                icon: <i className='bi bi-check-circle-fill' style={{ color: '#409895' }}></i>,
            });

        }
        const payload = response.data;
        return payload;
    } catch (error) {
        const errorMessage = error.response?.data?.serviceMessage || error.message;
        toast.error(errorMessage);
        useIntervalDispatcher(id, dispatch, milestone, 'fail');
        const { response } = error;
        toast.error(response?.data?.serviceMessage ? response?.data?.serviceMessage : error.message);
        return thunkAPI.rejectWithValue(error);
    }
});

export const uploadSupInviceBySup = createAsyncThunk('uploadSupInviceBySup', async (req, thunkAPI) => {
    const { id, data, dispatch, status, setSupplierInvoiceBtn, handleNext } = req;
    try {
        const response = await axios.put(`${baseUrlTra}/supplierinvoice/upload/${id}`, data, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });

        const payload = response.data;
        if (response) {
            toast.success(payload.serviceMessage, {
                icon: <span className='msg-icon float-start ms-2 sf-check success-icon'></span>,
            });
            dispatch(getsupplierInvoiceByOps(id));
            dispatch(gettransactionByID({ id, isUpdateRequired: true }));
            if (status === 'active') {
                localStorage.setItem('supInvoiceUlid', id);
                setSupplierInvoiceBtn(true);
                handleNext();
            }
        }
        return payload;
    } catch (error) {
        const errorMessage = error.response?.data?.serviceMessage || error.message;
        toast.error(errorMessage);
        return thunkAPI.rejectWithValue(errorMessage);
    }
});

export const cancelTransaction = createAsyncThunk('cancelTransaction', async (req, thunkAPI) => {
    const { id, data } = req;
    try {
        const response = await axios.put(`${baseUrlTra}/transaction/cancel?transaction_ulid=${id}`, data, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        const payload = response.data;
        if (response) {
            toast.success(payload.serviceMessage, {
                icon: <span className='msg-icon float-start ms-2 sf-check success-icon'></span>,
            });
            thunkAPI.dispatch(gettransactionByID({ id, isUpdateRequired: false }));
        }
        return payload;
    } catch (error) {
        const errorMessage = error.response?.data?.serviceMessage || error.message;
        toast.error(errorMessage);
        return thunkAPI.rejectWithValue(errorMessage);
    }
});

/**
 * Persist Delivery Tracking documents (KYC-style: files already uploaded to storage).
 * PUT /deliveryTracking/documents?transaction_ulid={id}
 * Body JSON: {
 *   artifacts: [{ document_ulid, document_name, file_name }],
 *   additional_documents: [{ document_ulid, document_name, file_name }]
 * }
 * On success: toast + refresh transaction (same pattern as cancel / other uploads).
 */
export const uploadDeliveryTrackingDocuments = createAsyncThunk(
    'uploadDeliveryTrackingDocuments',
    async (req, thunkAPI) => {
        const { id, data } = req;
        try {
            const response = await axios.post(
                `${baseUrlTra}/deliveryTracking/documents?transaction_ulid=${id}`,
                data,
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            );
            const payload = response.data;
            if (response) {
                toast.success(payload.serviceMessage, {
                    icon: <span className='msg-icon float-start ms-2 sf-check success-icon'></span>,
                });
                thunkAPI.dispatch(gettransactionByID({ id, isUpdateRequired: false }));
            }
            return payload;
        } catch (error) {
            const errorMessage = error.response?.data?.serviceMessage || error.message;
            toast.error(errorMessage);
            return thunkAPI.rejectWithValue(errorMessage);
        }
    }
);

export const getCancelTransactionDetails = createAsyncThunk(
    'getCancelTransactionDetails',
    async (transactionUlid, thunkAPI) => {
        try {
            const response = await axios.get(
                `${baseUrlTra}/transaction/cancel/details?transaction_ulid=${transactionUlid}`
            );
            return response.data?.payload;
        } catch (error) {
            const errorMessage = error.response?.data?.serviceMessage || error.message;
            toast.error(errorMessage);
            return thunkAPI.rejectWithValue(errorMessage);
        }
    }
);

export const getsupplierInvoiceByOps = createAsyncThunk('getsupplierInvoiceByOps', async (id, thunkAPI) => {
    try {
        const response = await axios.get(`${baseUrlTra}/supplierinvoice/${id}`);
        const { payload } = response.data;
        return payload;
    } catch (error) {
        const errorMessage = error.response?.data?.serviceMessage || error.message;
        return thunkAPI.rejectWithValue(errorMessage);
    }
});

export const saveSupplierInvoiceBySup = createAsyncThunk('saveSupplierInvoiceBySup', async (req, thunkAPI) => {
    const { id, data, dispatch, milestone } = req;
    useIntervalDispatcher(id, dispatch, milestone, 'success');
    try {
        const response = await axios.put(`${baseUrlTra}/supplierinvoice/save/${id}`, data, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        const payload = response.data;
        if (response) {
            dispatch(getsupplierInvoiceByOps(id));

        }

        return payload;
    } catch (error) {
        const errorMessage = error.response?.data?.serviceMessage || error.message;
        toast.error(errorMessage);
        useIntervalDispatcher(id, dispatch, milestone, 'fail');
        return thunkAPI.rejectWithValue(errorMessage);
    }
});

export const uploadBuyerInviceBySup = createAsyncThunk('uploadBuyerInviceBySup', async (req, thunkAPI) => {
    const { id, data, dispatch } = req;
    try {
        const response = await axios.put(`${baseUrlTra}/buyerInvoice/upload/${id}`, data, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });

        if (response) {
            toast.success(response.data.serviceMessage, {
                icon: <i className='bi bi-check-circle-fill' style={{ color: '#409895' }}></i>,
            });
            dispatch(getBuyerInvoiceByOps(id));
        }
        const payload = response.data;
        return payload;
    } catch (error) {
        const errorMessage = error.response?.data?.serviceMessage || error.message;
        toast.error(errorMessage);
        return thunkAPI.rejectWithValue(errorMessage);
    }
});

export const saveBuyerInvoiceBySup = createAsyncThunk('saveBuyerInvoiceBySup', async (req, thunkAPI) => {
    const { id, data, dispatch, milestone } = req;
    useIntervalDispatcher(id, dispatch, milestone, 'success');
    try {
        const response = await axios.put(`${baseUrlTra}/buyerInvoice/save/${id}`, data, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        const payload = response.data;
        if (response) {
            dispatch(getBuyerInvoiceByOps(id));

        }

        return payload;
    } catch (error) {
        const errorMessage = error.response?.data?.serviceMessage || error.message;
        toast.error(errorMessage);
        useIntervalDispatcher(id, dispatch, milestone, 'fail');
        return thunkAPI.rejectWithValue(errorMessage);
    }
});

export const getBuyerInvoiceByOps = createAsyncThunk('getBuyerInvoiceByOps', async (id, thunkAPI) => {
    try {
        const response = await axios.get(`${baseUrlTra}/buyerInvoice/${id}`);
        const { payload } = response.data;
        return payload;
    } catch (error) {
        const errorMessage = error.response?.data?.serviceMessage || error.message;
        return thunkAPI.rejectWithValue(errorMessage);
    }
});
export const getDealCalculation = createAsyncThunk('getDealCalculation', async (req, thunkAPI) => {
    try {
        const response = await axios.post(`${baseUrlTra}/dealcalculation`, req);
        const { payload } = response.data;
        if (response) {
            return payload;
        }
        return payload;
    } catch (error) {
        return thunkAPI.rejectWithValue(error);
    }
});
export const createPayment = createAsyncThunk('createPayment', async (req, thunkAPI) => {
    const { dispatch, query, data } = req;

    try {
        const response = await axios.post(`${baseUrlTra}/payment`, data);
        const { payload } = response.data;
        if (response) {
            dispatch(getPayment(query));

        }
        return payload;
    } catch (error) {
        const errorMessage = error.response?.data?.serviceMessage || error.message;
        toast.error(errorMessage);
        useIntervalDispatcher(query.transaction_ulid, dispatch, query.transaction_milestone, 'fail');

        return thunkAPI.rejectWithValue(errorMessage);
    }
});
export const getPayment = createAsyncThunk('getPayment', async (queryParams, { thunkAPI }) => {
    try {
        const response = await axios.get(`${baseUrlTra}/payment`, { params: queryParams });
        const { payload } = response.data;

        return payload;
    } catch (error) {
        return thunkAPI.rejectWithValue(error);
    }
});

export const dealclosure = createAsyncThunk('dealclosure', async (id, { dispatch, thunkAPI }) => {
    try {
        const response = await axios.put(`${baseUrlTra}/dealclosure/${id}`, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        const payload = response.data;
        const apiData = { id: id, isUpdateRequired: true };
        await dispatch(gettransactionByID(apiData));
        if (response) {
            toast.success(response.data.serviceMessage, {
                icon: <i className='bi bi-check-circle-fill' style={{ color: '#409895' }}></i>,
            });
        }

        return payload;
    } catch (error) {
        const errorMessage = error.response?.data?.serviceMessage || error.message;
        toast.error(errorMessage);
        return thunkAPI.rejectWithValue(errorMessage);
    }
});

export const getTotalCODAmount = createAsyncThunk('getTotalCODAmount', async (thunkAPI) => {
    try {
        const response = await axios.get(`${baseUrlTra}/getTotalCODAmount`);
        const { payload } = response.data;
        return payload;
    } catch (error) {
        const errorMessage = error.response?.data?.serviceMessage || error.message;
        return thunkAPI.rejectWithValue(errorMessage);
    }
});

export const getTotalMTTraded = createAsyncThunk('getTotalMTTraded', async (thunkAPI) => {
    try {
        const response = await axios.get(`${baseUrlTra}/getTotalMTTraded`);
        const { payload } = response.data;
        return payload;
    } catch (error) {
        const errorMessage = error.response?.data?.serviceMessage || error.message;
        return thunkAPI.rejectWithValue(errorMessage);
    }
});
export const dealfinancing = createAsyncThunk('dealfinancing', async (req, thunkAPI) => {
    try {
        const response = await axios.post(`${baseUrlTra}/dealfinancing`, req);
        const payload = response.data;
        if (response) {
            toast.success(response.data.serviceMessage, {
                icon: <i className='bi bi-check-circle-fill' style={{ color: '#409895' }}></i>,
            });
        }
        return payload;
    } catch (error) {
        const errorMessage = error.response?.data?.serviceMessage || error.message;
        toast.error(errorMessage);
        return thunkAPI.rejectWithValue(errorMessage);
    }
});
export const platformCharges = createAsyncThunk('platformCharges', async (thunkAPI) => {
    try {
        const response = await axios.get(`${baseUrlTra}/platformCharges`);
        const { payload } = response.data;
        return payload;
    } catch (error) {
        const errorMessage = error.response?.data?.serviceMessage || error.message;
        return thunkAPI.rejectWithValue(errorMessage);
    }
});
export const dFCByBuyer = createAsyncThunk('dFCByBuyer', async (request, thunkAPI) => {
    const { id, data, dispatch } = request;
    useIntervalDispatcher(id, dispatch, 'deal_financing_confirmation', 'success');
    try {
        const response = await axios.put(`${baseUrlTra}/dealFinancingConfirmationByBuyer/${id}`, data);

        const payload = response.data;
        return payload;
    } catch (error) {
        const errorMessage = error.response?.data?.serviceMessage || error.message;
        toast.error(errorMessage);
        useIntervalDispatcher(id, dispatch, 'deal_financing_confirmation', 'fail');
        return thunkAPI.rejectWithValue(errorMessage);
    }
});
export const dFCBySupplier = createAsyncThunk('dFCBySupplier', async (request, thunkAPI) => {
    const { id, data, dispatch } = request;
    useIntervalDispatcher(id, dispatch, 'deal_financing_confirmation', 'success');
    try {
        const response = await axios.put(`${baseUrlTra}/dealFinancingConfirmationBySupplier/${id}`, data);

        const payload = response.data;
        return payload;
    } catch (error) {
        const errorMessage = error.response?.data?.serviceMessage || error.message;
        toast.error(errorMessage);
        useIntervalDispatcher(id, dispatch, 'deal_financing_confirmation', 'fail');
        return thunkAPI.rejectWithValue(errorMessage);
    }
});
export const getTransactionsAddOnServiceDetails = createAsyncThunk(
    'getTransactionsAddOnServiceDetails',
    async (id, thunkAPI) => {
        try {
            const response = await axios.get(`${baseUrlTra}/getTransactionsAddOnServiceDetails/${id}`);
            const payload = response.data;
            return payload;
        } catch (error) {
            const errorMessage = error.response?.data?.serviceMessage || error.message;
            return thunkAPI.rejectWithValue(errorMessage);
        }
    }
);
export const getAssignPOCLists = createAsyncThunk('getAssignPOCLists', async (id, thunkAPI) => {
    try {
        const response = await axios.get(`${baseUrlTra}/getAssignPOCLists`);
        const payload = response.data;
        return payload;
    } catch (error) {
        const errorMessage = error.response?.data?.serviceMessage || error.message;
        return thunkAPI.rejectWithValue(errorMessage);
    }
});
export const updateAssignPOC = createAsyncThunk('updateAssignPOC', async (req, thunkAPI) => {
    const { dispatch, query, data } = req;

    try {
        const response = await axios.put(`${baseUrlTra}/updateAssignPOC`, data);
        const payload = response.data;
        if (payload) {
            const apiData = { id: query.transaction_ulid, isUpdateRequired: false };
            dispatch(gettransactionByID(apiData));
            toast.success(payload.serviceMessage, {
                icon: <i className='bi bi-check-circle-fill' style={{ color: '#409895' }}></i>,
            });
        }
        return payload;
    } catch (error) {
        toast.error(error.message);
        return thunkAPI.rejectWithValue(error);
    }
});
export const createVesselTracking = createAsyncThunk('createVesselTracking', async (req, thunkAPI) => {
    const { dispatch, query, data } = req;
    try {
        const response = await axios.post(`${baseUrlTra}/createVesselTracking`, data);
        const { payload } = response.data;
        if (response) {
            const apiData = { id: query.transaction_ulid, isUpdateRequired: false };
            dispatch(gettransactionByID(apiData));
            toast.success(response.data.serviceMessage, {
                icon: <i className='bi bi-check-circle-fill' style={{ color: '#409895' }}></i>,
            });
        }
        return payload;
    } catch (error) {
        const errorMessage = error.response?.data?.serviceMessage || error.message;
        toast.error(errorMessage);
        return thunkAPI.rejectWithValue(errorMessage);
    }
});
export const GetAgentDetails = createAsyncThunk('GetAgentDetails', async (id, thunkAPI) => {
    try {
        const response = await axios.get(`${baseUrlTra}/GetAgentDetails/${id}`);
        const { payload } = response.data;
        return payload;
    } catch (error) {
        return thunkAPI.rejectWithValue(error);
    }
});

export const getVesselTrackingLogs = createAsyncThunk('getVesselTrackingLogs', async (req, thunkAPI) => {
    const { transaction_ulid, role_type } = req;
    try {
        const response = await axios.get(`${baseUrlTra}/vesselTrackingLogs/${transaction_ulid}/${role_type}`);
        const { payload } = response.data;
        return payload;
    } catch (error) {
        const errorMessage = error.response?.data?.serviceMessage || error.message;
        return thunkAPI.rejectWithValue(errorMessage);
    }
});
